import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitMerge, Loader2, Package, Search, Mail, Phone, MapPin, X, TrendingUp, BarChart2, AlertCircle, CheckCircle, Plus, Store, List, Trash2, Merge, Inbox, Sparkles, Link2, CheckSquare, Square, FileText, Triangle, Minus, LayoutGrid, ChevronDown } from "lucide-react";
import { deleteSupplierProduct, fetchAllSupplierProducts, fetchBusinessDetails, fetchMenuCostingState, fetchProcessedInvoices, fetchStorageIntegrations, fetchSupplierProducts, mergeSupplierProduct, mergeSuppliers, resolveAllPending, resolveSupplierProduct, updateInvoiceLineCategory, updateSupplierFields, updateSupplierInvoiceLineCategory, type StorageIntegration } from "../../services/apiService";
import { Invoice, SupplierProduct } from "../../types";
import { InvoiceSlidingPanel } from "./InvoiceListPage";

type PageView = "suppliers" | "catalog" | "inbox";
type SupplierViewMode = "grid" | "list";
type SupplierSortRule = "name_az" | "spend_high" | "invoices_high" | "products_high" | "recent_order";
type SupplierProductWithSupplierName = SupplierProduct & { supplierName: string | null };
import { CATEGORY_COLORS, getCategoriesForIndustry } from "../../data/categories";
import { useBusiness } from "../../contexts/BusinessContext";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const WARN = "#a07428";
const WARN_WEAK = "rgba(160,116,40,0.12)";
const BACKDROP = "rgba(80,55,30,0.45)";
const SUPPLIER_LIST_COLUMNS = "minmax(220px,1.25fr) minmax(140px,.75fr) minmax(140px,.75fr) minmax(220px,1.1fr) minmax(130px,.6fr) minmax(120px,.6fr)";

// ── Helpers ───────────────────────────────────────────────────────────────────

function numberValue(value: number | null | undefined) {
  return value ?? 0;
}

function displayCurrency(value: number | null | undefined) {
  return value === null || value === undefined ? "null" : `$${value.toLocaleString()}`;
}

function displayText(value: string | number | null | undefined) {
  return value === null || value === undefined || value === "" ? "null" : String(value);
}

function PriceDirectionIcon({ current, previous }: { current: number | null | undefined; previous: number | null | undefined }) {
  if (current == null || previous == null || Math.abs(current - previous) < 0.0001) {
    return <Minus size={11} strokeWidth={2.2} style={{ color: MUTED }} />;
  }

  const isUp = current > previous;
  return (
    <Triangle
      size={10}
      fill="currentColor"
      strokeWidth={0}
      style={{
        color: isUp ? UP : DOWN,
        transform: isUp ? undefined : "rotate(180deg)",
      }}
    />
  );
}

function updateInvoiceCategoryCache(
  invoices: Invoice[] | undefined,
  category: string,
  shouldUpdateLine: (invoice: Invoice, lineId: string) => boolean,
) {
  if (!invoices) return invoices;

  return invoices.map((invoice) => {
    let changed = false;
    const lineItems = invoice.lineItems.map((line) => {
      if (!shouldUpdateLine(invoice, line.id)) return line;
      changed = true;
      return { ...line, category };
    });
    return changed ? { ...invoice, lineItems } : invoice;
  });
}

type SupplierFieldUpdates = {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxNumber?: string;
  category?: string;
};

function updateInvoiceSupplierCache(
  invoices: Invoice[] | undefined,
  supplierId: string,
  updates: SupplierFieldUpdates,
) {
  if (!invoices) return invoices;

  return invoices.map((invoice) => (
    invoice.supplier.id === supplierId
      ? { ...invoice, supplier: { ...invoice.supplier, ...updates } }
      : invoice
  ));
}

// ── Derived types ─────────────────────────────────────────────────────────────

interface ProductEntry {
  lineIds: string[];
  productCode: string;
  description: string;
  category: string;
  latestUnitPrice: number;
  unit: string;
  totalSpend: number;
  lastSeenDate: string;
  timesOrdered: number;
}

interface SupplierEntry {
  id: string;
  name: string;
  code: string;
  email: string;
  phone: string;
  address: string;
  taxNumber: string;
  category: string;
  invoiceCount: number;
  totalSpend: number;
  lastInvoiceDate: string;
  products: ProductEntry[];
  monthlySpend: { month: string; amount: number }[];
}

// ── Aggregation ───────────────────────────────────────────────────────────────

function buildSuppliers(invoices: Invoice[]): SupplierEntry[] {
  const supplierMap = new Map<string, SupplierEntry>();
  const monthMaps = new Map<string, Map<string, number>>();

  for (const inv of invoices) {
    const sid = inv.supplier.id;

    if (!supplierMap.has(sid)) {
      supplierMap.set(sid, {
        id: sid,
        name: inv.supplier.name,
        code: inv.supplier.code,
        email: inv.supplier.email,
        phone: inv.supplier.phone || "",
        address: inv.supplier.address || "",
        taxNumber: inv.supplier.taxNumber || "",
        category: inv.supplier.category,
        invoiceCount: 0,
        totalSpend: 0,
        lastInvoiceDate: inv.invoiceDate ?? "null",
        products: [],
        monthlySpend: [],
      });
      monthMaps.set(sid, new Map<string, number>());
    }

    const entry = supplierMap.get(sid)!;
    entry.invoiceCount += 1;
    entry.totalSpend += numberValue(inv.totalAmount);
    if (inv.invoiceDate && inv.invoiceDate > entry.lastInvoiceDate) entry.lastInvoiceDate = inv.invoiceDate;

    if (inv.invoiceDate) {
      const month = inv.invoiceDate.slice(0, 7);
      const mm = monthMaps.get(sid)!;
      mm.set(month, (mm.get(month) ?? 0) + numberValue(inv.totalAmount));
    }

    for (const item of inv.lineItems) {
      const code = item.productCode ?? `${sid}-UNKNOWN`;
      const existing = entry.products.find((p) => p.productCode === code);

      if (existing) {
        existing.lineIds.push(item.id);
        existing.totalSpend  += numberValue(item.totalPrice);
        existing.timesOrdered += 1;
        existing.latestUnitPrice = numberValue(item.unitPrice);
        if (inv.invoiceDate && inv.invoiceDate > existing.lastSeenDate) existing.lastSeenDate = inv.invoiceDate;
      } else {
        entry.products.push({
          lineIds: [item.id],
          productCode: code,
          description: item.description ?? "null",
          category: item.category || "Other",
          latestUnitPrice: numberValue(item.unitPrice),
          unit: item.unit ?? "null",
          totalSpend: numberValue(item.totalPrice),
          lastSeenDate: inv.invoiceDate ?? "null",
          timesOrdered: 1,
        });
      }
    }
  }

  Array.from(supplierMap.values()).forEach((entry) => {
    entry.products.sort((a, b) => a.productCode.localeCompare(b.productCode));
    const mm = monthMaps.get(entry.id)!;
    entry.monthlySpend = [...mm.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, amount]) => ({ month, amount }));
  });

  return Array.from(supplierMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function EditableInput({
  value,
  placeholder,
  onSave,
}: {
  value: string | null | undefined;
  placeholder?: string;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  function save() {
    const next = draft.trim();
    if (next !== (value || "")) onSave(next);
  }

  return (
    <input
      value={draft}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(value || "");
          e.currentTarget.blur();
        }
      }}
      className="w-full rounded-md px-2 py-1.5 text-xs outline-none transition-all"
      style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
    />
  );
}

function CategorySelect({
  value,
  onSave,
  categories,
}: {
  value: string;
  onSave: (value: string) => void;
  categories: string[];
}) {
  const dotColor = CATEGORY_COLORS[value] ?? CATEGORY_COLORS["Other"];
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: dotColor }}
      />
      <select
        value={value || "Other"}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onSave(e.target.value)}
        className="w-full rounded-md px-2 py-1.5 text-xs font-semibold outline-none"
        style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
      >
        {categories.map((category) => (
          <option key={category} value={category}>{category}</option>
        ))}
      </select>
    </div>
  );
}

// ── SupplierCard ──────────────────────────────────────────────────────────────

function SupplierCard({
  supplier,
  onClick,
}: {
  supplier: SupplierEntry;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const catColor = CATEGORY_COLORS[supplier.category] ?? CATEGORY_COLORS["Other"];
  const initials = supplier.name.slice(0, 2).toUpperCase();

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="aspect-square rounded-lg p-4 flex flex-col"
      style={{
        cursor: "pointer",
        backgroundColor: SURFACE,
        border: `1px solid ${LINE}`,
        boxShadow: hovered
          ? "0 10px 28px rgba(80,55,30,0.10)"
          : SHADOW,
        transition: "box-shadow 0.15s ease",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm"
          style={{ backgroundColor: `${catColor}18`, color: catColor }}
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm leading-snug truncate" style={{ color: NAVY }}>
            {supplier.name}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: catColor }}
            />
            <span className="text-[11px] truncate" style={{ color: NAVY, opacity: 0.6 }}>
              {supplier.category}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>
            Total Spend
          </p>
          <p className="text-lg font-extrabold mt-0.5 leading-none" style={{ color: NAVY }}>
            {displayCurrency(supplier.totalSpend)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>
            Invoices
          </p>
          <p className="text-lg font-extrabold mt-0.5 leading-none" style={{ color: NAVY }}>
            {supplier.invoiceCount}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>
            Products
          </p>
          <p className="text-base font-bold mt-0.5 leading-none" style={{ color: NAVY }}>
            {supplier.products.length}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>
            Last Order
          </p>
          <p className="text-[11px] font-semibold mt-0.5 leading-none" style={{ color: NAVY, opacity: 0.7 }}>
            {displayText(supplier.lastInvoiceDate)}
          </p>
        </div>
      </div>

      <div className="mt-5 flex justify-start">
        <span className="text-[11px] font-semibold" style={{ color: ORANGE }}>
          View details →
        </span>
      </div>
    </div>
  );
}

function supplierIsActive(supplier: SupplierEntry) {
  const lastOrder = new Date(supplier.lastInvoiceDate);
  if (Number.isNaN(lastOrder.getTime())) return false;

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  return lastOrder >= cutoff;
}

function SupplierListRow({
  supplier,
  index,
  onClick,
}: {
  supplier: SupplierEntry;
  index: number;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const catColor = CATEGORY_COLORS[supplier.category] ?? CATEGORY_COLORS["Other"];
  const rowBackground = SURFACE;
  const active = supplierIsActive(supplier);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="grid w-full items-center gap-4 px-5 py-3.5 text-left transition-all"
      style={{
        gridTemplateColumns: SUPPLIER_LIST_COLUMNS,
        backgroundColor: rowBackground,
        border: `1.5px solid ${hovered ? ACCENT_LINE : "transparent"}`,
        boxShadow: hovered ? "0 7px 18px rgba(96,120,64,0.08)" : "none",
      }}
    >
      <span className="truncate text-sm font-bold" style={{ color: NAVY }}>
        {supplier.name}
      </span>
      <span className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold" style={{ color: NAVY, opacity: 0.62 }}>
        <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: catColor }} />
        <span className="truncate">{supplier.category || "Other"}</span>
      </span>
      <span className="truncate text-sm font-semibold" style={{ color: NAVY, opacity: 0.58 }}>
        {displayText(supplier.phone)}
      </span>
      <span className="truncate text-sm" style={{ color: NAVY, opacity: 0.58 }}>
        {displayText(supplier.email)}
      </span>
      <span className="text-sm font-semibold tabular-nums" style={{ color: NAVY, opacity: 0.7 }}>
        {displayText(supplier.lastInvoiceDate)}
      </span>
      <span
        className="inline-flex items-center gap-2 text-sm font-semibold"
        style={{ color: NAVY, opacity: active ? 0.68 : 0.42 }}
        title="An active supplier is one that has had an order in the last 30 days."
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: active ? ORANGE : MUTED }} />
        {active ? "Active" : "Inactive"}
      </span>
    </button>
  );
}

// ── SupplierModal ─────────────────────────────────────────────────────────────

function SupplierModal({
  supplier,
  businessId,
  categories,
  onClose,
}: {
  supplier: SupplierEntry;
  businessId?: string;
  categories: string[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"info" | "products" | "analytics">("info");
  const [productSearch, setProductSearch] = useState("");
  const [saveError, setSaveError] = useState("");
  const [tagInputCode, setTagInputCode] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState("");
  const queryClient = useQueryClient();

  const allLineIds = supplier.products.flatMap((p) => p.lineIds);
  const invoicesQueryKey = ["invoices", businessId] as const;

  const { data: catalogItemsData } = useQuery<SupplierProduct[]>({
    queryKey: ["supplier-products", supplier.id, businessId],
    queryFn: () => fetchSupplierProducts(supplier.id, businessId),
    enabled: Boolean(supplier.id),
    staleTime: 30_000,
  });
  const catalogItems: SupplierProduct[] = catalogItemsData ?? [];

  const catalogByCode = useMemo(
    () => new Map(catalogItems.map((c) => [c.productCode, c])),
    [catalogItems],
  );

  const pendingCount = catalogItems.filter((c) => c.status === "new" || c.status === "conflict").length;

  const resolveMutation = useMutation({
    mutationFn: ({
      productId,
      action,
      description,
      tags,
    }: {
      productId: string;
      action: "confirm" | "dismiss";
      description?: string;
      tags?: string[];
    }) => resolveSupplierProduct(supplier.id, productId, action, { description, tags }, businessId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["supplier-products", supplier.id, businessId] }),
  });

  const supplierMutation = useMutation({
    mutationFn: (updates: SupplierFieldUpdates) =>
      updateSupplierFields(supplier.id, updates, businessId),
    onMutate: async (updates) => {
      setSaveError("");
      await queryClient.cancelQueries({ queryKey: invoicesQueryKey });
      const previousInvoices = queryClient.getQueryData<Invoice[]>(invoicesQueryKey);
      queryClient.setQueryData<Invoice[]>(
        invoicesQueryKey,
        (current: Invoice[] | undefined) => updateInvoiceSupplierCache(current, supplier.id, updates),
      );
      return { previousInvoices };
    },
    onSuccess: (updatedSupplier) => {
      setSaveError("");
      queryClient.setQueryData<Invoice[]>(
        invoicesQueryKey,
        (current: Invoice[] | undefined) => updateInvoiceSupplierCache(current, supplier.id, {
          name: updatedSupplier.name,
          email: updatedSupplier.email,
          phone: updatedSupplier.phone || "",
          address: updatedSupplier.address || "",
          taxNumber: updatedSupplier.taxNumber || "",
          category: updatedSupplier.category,
        }),
      );
    },
    onError: (error: Error, _updates, context) => {
      if (context?.previousInvoices) queryClient.setQueryData(invoicesQueryKey, context.previousInvoices);
      setSaveError(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey });
    },
  });

  const productCategoryMutation = useMutation({
    mutationFn: ({ lineIds, category }: { lineIds: string[]; category: string }) =>
      updateInvoiceLineCategory(lineIds, category, businessId),
    onMutate: async ({ lineIds, category }) => {
      setSaveError("");
      await queryClient.cancelQueries({ queryKey: invoicesQueryKey });
      const previousInvoices = queryClient.getQueryData<Invoice[]>(invoicesQueryKey);
      const lineIdSet = new Set(lineIds);
      queryClient.setQueryData<Invoice[]>(
        invoicesQueryKey,
        (current: Invoice[] | undefined) => updateInvoiceCategoryCache(current, category, (_invoice, lineId) => lineIdSet.has(lineId)),
      );
      return { previousInvoices };
    },
    onSuccess: () => {
      setSaveError("");
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousInvoices) queryClient.setQueryData(invoicesQueryKey, context.previousInvoices);
      setSaveError(error.message);
    },
  });

  const setAllCategoryMutation = useMutation({
    mutationFn: ({ category }: { category: string }) =>
      updateSupplierInvoiceLineCategory(supplier.id, category, businessId),
    onMutate: async ({ category }) => {
      setSaveError("");
      await queryClient.cancelQueries({ queryKey: invoicesQueryKey });
      const previousInvoices = queryClient.getQueryData<Invoice[]>(invoicesQueryKey);
      queryClient.setQueryData<Invoice[]>(
        invoicesQueryKey,
        (current: Invoice[] | undefined) => updateInvoiceCategoryCache(current, category, (invoice) => invoice.supplier.id === supplier.id),
      );
      return { previousInvoices };
    },
    onSuccess: () => {
      setSaveError("");
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary", businessId] });
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousInvoices) queryClient.setQueryData(invoicesQueryKey, context.previousInvoices);
      setSaveError(error.message);
    },
  });

  const catColor = CATEGORY_COLORS[supplier.category] ?? CATEGORY_COLORS["Other"];
  const initials = supplier.name.slice(0, 2).toUpperCase();

  const filteredProducts = productSearch.trim()
    ? supplier.products.filter(
        (p) =>
          p.description.toLowerCase().includes(productSearch.toLowerCase()) ||
          p.productCode.toLowerCase().includes(productSearch.toLowerCase()) ||
          p.category.toLowerCase().includes(productSearch.toLowerCase()),
      )
    : supplier.products;

  const last12Months = useMemo(() => {
    const now = new Date();
    const months: string[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return months.map((m) => {
      const found = supplier.monthlySpend.find((ms) => ms.month === m);
      return { month: m, amount: found?.amount ?? 0 };
    });
  }, [supplier.monthlySpend]);

  const maxMonthlySpend = Math.max(...last12Months.map((m) => m.amount), 1);

  const categoryBreakdown = useMemo(() => {
    const map = new Map<string, { spend: number; count: number }>();
    for (const p of supplier.products) {
      const cat = p.category || "Other";
      const existing = map.get(cat);
      if (existing) {
        existing.spend += p.totalSpend;
        existing.count += 1;
      } else {
        map.set(cat, { spend: p.totalSpend, count: 1 });
      }
    }
    return [...map.entries()]
      .map(([cat, { spend, count }]) => ({ cat, spend, count }))
      .sort((a, b) => b.spend - a.spend);
  }, [supplier.products]);

  const maxCatSpend = Math.max(...categoryBreakdown.map((c) => c.spend), 1);

  const tabs: { key: "info" | "products" | "analytics"; label: string }[] = [
    { key: "info", label: "Info & Contact" },
    { key: "products", label: `Products (${supplier.products.length})` },
    { key: "analytics", label: "Analytics" },
  ];

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ backgroundColor: BACKDROP }}
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: "85vh", backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
      >
        <div
          className="flex-shrink-0 px-6 py-5 flex items-center justify-between gap-4"
          style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}
        >
          <div className="flex items-center gap-4 min-w-0">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-base"
              style={{ backgroundColor: `${catColor}18`, color: catColor }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-extrabold leading-snug truncate" style={{ color: NAVY }}>
                {supplier.name}
              </p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: catColor }} />
                  <span className="text-[11px]" style={{ color: NAVY, opacity: 0.6 }}>{supplier.category}</span>
                </div>
                <span style={{ color: NAVY, opacity: 0.3 }}>·</span>
                <span className="text-[11px]" style={{ color: NAVY, opacity: 0.6 }}>
                  {supplier.invoiceCount} invoice{supplier.invoiceCount !== 1 ? "s" : ""}
                </span>
                <span style={{ color: NAVY, opacity: 0.3 }}>·</span>
                <span className="text-[11px] font-semibold" style={{ color: NAVY, opacity: 0.75 }}>
                  {displayCurrency(supplier.totalSpend)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => { onClose(); navigate("/invoices", { state: { supplierId: supplier.id, supplierName: supplier.name } }); }}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
              style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
            >
              <FileText size={13} />
              View invoices
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex gap-0 px-6 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}` }}>
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="px-4 pb-3.5 pt-4 text-xs font-semibold transition-all duration-150 mr-1"
                style={{
                  color: active ? ORANGE : MUTED,
                  borderBottom: active ? `2px solid ${ORANGE}` : "2px solid transparent",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "info" && (
            <div>
              {[
                { label: "Category", content: <CategorySelect value={supplier.category} onSave={(category) => supplierMutation.mutate({ category })} categories={categories} /> },
              ].map(({ label, content }) => (
                <div key={label} className="flex items-center gap-4 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
                  <span className="text-[11px] uppercase tracking-wide font-semibold flex-shrink-0 w-28" style={{ color: NAVY, opacity: 0.45 }}>{label}</span>
                  <div className="flex-1">{content}</div>
                </div>
              ))}
              {[
                { label: "Email",      icon: <Mail size={13} style={{ color: MUTED, flexShrink: 0 }} />,  value: supplier.email,     onSave: (v: string) => supplierMutation.mutate({ email: v }),      placeholder: "Email" },
                { label: "Phone",      icon: <Phone size={13} style={{ color: MUTED, flexShrink: 0 }} />, value: supplier.phone,     onSave: (v: string) => supplierMutation.mutate({ phone: v }),      placeholder: "Phone" },
                { label: "Address",    icon: <MapPin size={13} style={{ color: MUTED, flexShrink: 0 }} />,value: supplier.address,   onSave: (v: string) => supplierMutation.mutate({ address: v }),    placeholder: "Address" },
              ].map(({ label, icon, value, onSave, placeholder }) => (
                <div key={label} className="flex items-center gap-4 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
                  <span className="text-[11px] uppercase tracking-wide font-semibold flex-shrink-0 w-28" style={{ color: NAVY, opacity: 0.45 }}>{label}</span>
                  <div className="flex items-center gap-2 flex-1">
                    {icon}
                    <div className="flex-1"><EditableInput value={value} placeholder={placeholder} onSave={onSave} /></div>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
                <span className="text-[11px] uppercase tracking-wide font-semibold flex-shrink-0 w-28" style={{ color: NAVY, opacity: 0.45 }}>Tax Number</span>
                <div className="flex-1"><EditableInput value={supplier.taxNumber} placeholder="Tax Number" onSave={(v) => supplierMutation.mutate({ taxNumber: v })} /></div>
              </div>
              {saveError && <p className="text-[11px] mt-3 leading-snug" style={{ color: UP }}>{saveError}</p>}
            </div>
          )}

          {activeTab === "products" && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: NAVY, opacity: 0.4 }} />
                  <input
                    type="text"
                    placeholder="Search products…"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 text-xs rounded-lg outline-none"
                    style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                    onFocus={(e) => (e.target.style.border = `1.5px solid ${ORANGE}`)}
                    onBlur={(e) => (e.target.style.border = `1.5px solid ${LINE}`)}
                  />
                </div>
                <button
                  type="button"
                  disabled={allLineIds.length === 0 || setAllCategoryMutation.isPending}
                  onClick={() => setAllCategoryMutation.mutate({ category: supplier.category || "Other" })}
                  className="rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-all disabled:opacity-40 flex-shrink-0"
                  style={{ border: `1px solid ${ACCENT_LINE}`, color: ORANGE, backgroundColor: ACCENT_WEAK }}
                >
                  {setAllCategoryMutation.isPending ? "Updating…" : `Set all to ${supplier.category || "Other"}`}
                </button>
              </div>

              {filteredProducts.length === 0 ? (
                <div className="py-16 text-center">
                  <Package size={32} className="mx-auto mb-3" style={{ color: NAVY, opacity: 0.2 }} />
                  <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.4 }}>
                    {productSearch ? "No products match your search." : "No product data extracted yet."}
                  </p>
                </div>
              ) : (
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
                  <table className="w-full table-fixed text-sm">
                    <colgroup>
                      <col className="w-[10%]" /><col className="w-[32%]" /><col className="w-[24%]" />
                      <col className="w-[7%]" /><col className="w-[9%]" /><col className="w-[9%]" /><col className="w-[9%]" />
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                        {[
                          { label: "Product Code", cls: "text-left" },
                          { label: "Description",  cls: "text-left" },
                          { label: "Category",     cls: "text-left" },
                          { label: "Unit",         cls: "text-center" },
                          { label: "Unit Price",   cls: "text-right" },
                          { label: "Total Spend",  cls: "text-right" },
                          { label: "Last Ordered", cls: "text-center" },
                        ].map(({ label, cls }) => (
                          <th key={label} className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-wide ${cls}`} style={{ color: NAVY, opacity: 0.45 }}>
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((p) => (
                        <tr
                          key={p.productCode}
                          style={{ borderTop: `1px solid ${LINE}` }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ACCENT_WEAK)}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                        >
                          <td className="px-4 py-2.5">
                            <span className="text-xs font-semibold tabular-nums" style={{ color: NAVY }}>{p.productCode}</span>
                          </td>
                          <td className="px-4 py-2.5 text-sm leading-snug" style={{ color: NAVY }}>
                            <span className="block break-words">{p.description}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <CategorySelect
                              value={p.category || supplier.category}
                              onSave={(category) => productCategoryMutation.mutate({ lineIds: p.lineIds, category })}
                              categories={categories}
                            />
                          </td>
                          <td className="px-4 py-2.5 text-center text-xs" style={{ color: NAVY, opacity: 0.6 }}>{p.unit}</td>
                          <td className="px-4 py-2.5 text-right font-semibold" style={{ color: NAVY }}>{displayCurrency(p.latestUnitPrice)}</td>
                          <td className="px-4 py-2.5 text-right font-bold" style={{ color: NAVY }}>{displayCurrency(p.totalSpend)}</td>
                          <td className="px-4 py-2.5 text-center text-xs" style={{ color: NAVY, opacity: 0.55 }}>{displayText(p.lastSeenDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "analytics" && (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { label: "Avg Invoice", value: displayCurrency(supplier.invoiceCount > 0 ? Math.round(supplier.totalSpend / supplier.invoiceCount) : 0) },
                  { label: "Products",    value: String(supplier.products.length) },
                  { label: "Last Order",  value: displayText(supplier.lastInvoiceDate) },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-xl px-4 py-3" style={{ backgroundColor: RAISED, border: `1px solid ${LINE}` }}>
                    <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>{label}</p>
                    <p className="text-base font-extrabold mt-1" style={{ color: NAVY }}>{value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={14} style={{ color: ORANGE }} />
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.6 }}>Monthly Spend</p>
                  </div>
                  <div className="flex items-end gap-1 h-32">
                    {last12Months.map(({ month, amount }) => {
                      const barH = maxMonthlySpend > 0 ? (amount / maxMonthlySpend) * 120 : 0;
                      return (
                        <div key={month} className="flex flex-col items-center flex-1 min-w-0">
                          <div
                            className="w-full rounded-t-sm transition-all duration-300"
                            style={{ height: `${barH}px`, maxHeight: "120px", backgroundColor: amount > 0 ? ORANGE : CHIP }}
                            title={`${month}: ${displayCurrency(amount)}`}
                          />
                          <span className="text-[9px] mt-1 leading-none" style={{ color: NAVY, opacity: 0.45 }}>
                            {month.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart2 size={14} style={{ color: ORANGE }} />
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.6 }}>By Category</p>
                  </div>
                  {categoryBreakdown.length === 0 ? (
                    <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>No product data available.</p>
                  ) : (
                    <div className="space-y-3">
                      {categoryBreakdown.map(({ cat, spend, count }) => {
                        const dotColor = CATEGORY_COLORS[cat] ?? CATEGORY_COLORS["Other"];
                        const pct = maxCatSpend > 0 ? (spend / maxCatSpend) * 100 : 0;
                        return (
                          <div key={cat}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
                                <span className="text-[11px] font-semibold truncate" style={{ color: NAVY }}>{cat}</span>
                                <span className="text-[10px] rounded-full px-1.5 py-0.5 font-bold flex-shrink-0" style={{ backgroundColor: CHIP, color: NAVY, opacity: 0.7 }}>{count}</span>
                              </div>
                              <span className="text-[11px] font-bold flex-shrink-0 ml-2" style={{ color: NAVY }}>{displayCurrency(spend)}</span>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: CHIP }}>
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: dotColor }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MergeSuppliersModal ───────────────────────────────────────────────────────

function MergeSuppliersModal({
  suppliers,
  businessId,
  onClose,
}: {
  suppliers: SupplierEntry[];
  businessId?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [targetSupplierId, setTargetSupplierId] = useState("");
  const [sourceSupplierIds, setSourceSupplierIds] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");

  const target = suppliers.find((s) => s.id === targetSupplierId);
  const sourceSuppliers = suppliers.filter((s) => sourceSupplierIds.includes(s.id));
  const movedInvoiceCount = sourceSuppliers.reduce((sum, s) => sum + s.invoiceCount, 0);
  const confirmationReady = Boolean(target) && confirmed && sourceSuppliers.length > 0;

  const mergeMutation = useMutation({
    mutationFn: () => mergeSuppliers({ targetSupplierId, sourceSupplierIds }, businessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices", businessId] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary", businessId] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  function toggleSource(id: string) {
    setError(""); setConfirmed(false);
    setSourceSupplierIds((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);
  }

  function submit() {
    setError("");
    const cleaned = sourceSupplierIds.filter((id) => id !== targetSupplierId);
    if (!targetSupplierId || cleaned.length === 0) { setError("Choose what to merge from and the final true supplier."); return; }
    setSourceSupplierIds(cleaned);
    mergeMutation.mutate();
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto px-4 py-8" style={{ backgroundColor: BACKDROP }}>
      <div className="w-full max-w-2xl rounded-xl shadow-xl" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
        <div className="flex items-start justify-between gap-4 px-5 py-4" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
          <div>
            <p className="text-sm font-extrabold" style={{ color: NAVY }}>Merge suppliers</p>
            <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.5 }}>
              Move invoices from duplicate AI-created suppliers into one final supplier.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 transition-colors" style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
            <X size={14} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-5">
          <div>
            <p className="text-xs font-bold mb-2" style={{ color: NAVY }}>Merge from</p>
            <div className="max-h-80 overflow-y-auto rounded-lg" style={{ border: `1.5px solid ${LINE}` }}>
              {suppliers.map((s, index) => {
                const checked = sourceSupplierIds.includes(s.id);
                const disabled = s.id === targetSupplierId;
                return (
                  <label key={s.id} className="flex cursor-pointer items-start gap-3 px-3 py-3" style={{ borderTop: index > 0 ? `1px solid ${LINE}` : undefined, opacity: disabled ? 0.35 : 1 }}>
                    <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleSource(s.id)} className="mt-1" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold" style={{ color: NAVY }}>{s.name}</span>
                      <span className="block text-[11px]" style={{ color: NAVY, opacity: 0.5 }}>{s.invoiceCount} invoices · {displayCurrency(s.totalSpend)}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold mb-2" style={{ color: NAVY }}>Final true supplier</p>
            <select
              value={targetSupplierId}
              onChange={(e) => { setError(""); setConfirmed(false); setTargetSupplierId(e.target.value); setSourceSupplierIds((ids) => ids.filter((id) => id !== e.target.value)); }}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none"
              style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
            >
              <option value="">Choose final supplier…</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.invoiceCount} invoices</option>)}
            </select>

            <div className="mt-4 rounded-lg px-4 py-3" style={{ backgroundColor: RAISED, border: `1px solid ${LINE}` }}>
              <p className="text-xs font-bold" style={{ color: NAVY }}>Merge summary</p>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: NAVY, opacity: 0.58 }}>
                {sourceSuppliers.length === 0 || !target
                  ? "Choose duplicate suppliers and the final supplier to preview the merge."
                  : `${sourceSuppliers.length} supplier${sourceSuppliers.length === 1 ? "" : "s"} and ${movedInvoiceCount} invoice${movedInvoiceCount === 1 ? "" : "s"} will move into ${target.name}.`}
              </p>
            </div>

            {target && sourceSuppliers.length > 0 && (
              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg px-4 py-3" style={{ backgroundColor: WARN_WEAK, border: `1px solid ${WARN}33` }}>
                <input type="checkbox" checked={confirmed} onChange={(e) => { setError(""); setConfirmed(e.target.checked); }} className="mt-0.5 shrink-0" />
                <p className="text-xs leading-relaxed" style={{ color: WARN }}>
                  I understand this merge cannot be undone — all invoices from the selected suppliers will move into <strong>{target.name}</strong>.
                </p>
              </label>
            )}

            {error && <p className="text-xs mt-3" style={{ color: UP }}>{error}</p>}

            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={submit}
                disabled={mergeMutation.isPending || !confirmationReady}
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
                style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
              >
                {mergeMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <GitMerge size={13} />}
                {mergeMutation.isPending ? "Merging..." : "Merge suppliers"}
              </button>
              <button type="button" onClick={onClose} disabled={mergeMutation.isPending} className="rounded-lg px-4 py-2 text-xs font-bold transition-all disabled:opacity-50" style={{ color: NAVY, backgroundColor: CHIP }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────



export default function SuppliersPage() {
  const { activeBusiness } = useBusiness();
  const location = useLocation();
  const navigate = useNavigate();
  const urlView = (new URLSearchParams(location.search).get("view") ?? "suppliers") as PageView;
  const [pageView, setPageView] = useState<PageView>(urlView);

  useEffect(() => {
    const v = (new URLSearchParams(location.search).get("view") ?? "suppliers") as PageView;
    setPageView(v);
  }, [location.search]);

  function changeView(v: PageView) {
    setPageView(v);
    navigate(v === "suppliers" ? "/suppliers" : `/suppliers?view=${v}`, { replace: true });
  }
  const [search, setSearch] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogStatus, setCatalogStatus] = useState("all");
  const [tagInput, setTagInput] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [mergeSearch, setMergeSearch] = useState("");
  const [catalogSupplier, setCatalogSupplier] = useState("all");
  const [catalogLayer, setCatalogLayer] = useState("");
  const [catalogGroup, setCatalogGroup] = useState("");
  const [expandedCatalogId, setExpandedCatalogId] = useState<string | null>(null);
  const [viewInvoice, setViewInvoice] = useState<{ invoiceId: string; productCode: string } | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierEntry | null>(null);
  const [supplierView, setSupplierView] = useState<SupplierViewMode>("grid");
  const [supplierCategoryFilter, setSupplierCategoryFilter] = useState("all");
  const [supplierSort, setSupplierSort] = useState<SupplierSortRule>("name_az");
  const [selectedInboxIds, setSelectedInboxIds] = useState<Set<string>>(new Set());
  const [batchPending, setBatchPending] = useState(false);
  const queryClient = useQueryClient();

  const { data: businessDetails } = useQuery({
    queryKey: ["business-details", activeBusiness?.id],
    queryFn: () => fetchBusinessDetails(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
    staleTime: 5 * 60_000,
  });
  const categories = getCategoriesForIndustry(businessDetails?.industry);

  const { data: invoicesData, error: invoicesError } = useQuery<Invoice[]>({
    queryKey: ["invoices", activeBusiness?.id],
    queryFn: () => fetchProcessedInvoices(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });
  const invoices: Invoice[] = invoicesData ?? [];
  const { data: storageIntegrationsData } = useQuery<StorageIntegration[]>({
    queryKey: ["storage-integrations", activeBusiness?.id],
    queryFn: () => fetchStorageIntegrations(activeBusiness?.id),
    enabled: Boolean(activeBusiness?.id),
  });
  const storageIntegrations: StorageIntegration[] = storageIntegrationsData ?? [];
  const deletesExternalOnInvoiceDelete = storageIntegrations.some((integration) =>
    integration.status === "connected" && integration.deleteOnInvoiceDelete
  );

  const { data: allCatalogData } = useQuery<SupplierProductWithSupplierName[]>({
    queryKey: ["all-supplier-products", activeBusiness?.id],
    queryFn: () => fetchAllSupplierProducts(activeBusiness?.id),
    enabled: Boolean(activeBusiness) && (pageView === "catalog" || pageView === "inbox"),
    staleTime: 30_000,
  });
  const allCatalog: SupplierProductWithSupplierName[] = allCatalogData ?? [];

  const { data: menuCostingData } = useQuery({
    queryKey: ["menu-costing", activeBusiness?.id],
    queryFn: () => fetchMenuCostingState(activeBusiness?.id),
    enabled: Boolean(activeBusiness) && (pageView === "catalog" || pageView === "inbox"),
    staleTime: 5 * 60_000,
  });

  const catalogResolveMutation = useMutation({
    mutationFn: ({ supplierId, productId, action, description, tags }: { supplierId: string; productId: string; action: "confirm" | "dismiss"; description?: string; tags?: string[] }) =>
      resolveSupplierProduct(supplierId, productId, action, { description, tags }, activeBusiness?.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["all-supplier-products", activeBusiness?.id] }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ supplierId, productId }: { supplierId: string; productId: string }) =>
      deleteSupplierProduct(supplierId, productId, activeBusiness?.id),
    onSuccess: () => {
      setConfirmDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ["all-supplier-products", activeBusiness?.id] });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ supplierId, productId, mergeIntoProductId }: { supplierId: string; productId: string; mergeIntoProductId: string }) =>
      mergeSupplierProduct(supplierId, productId, mergeIntoProductId, activeBusiness?.id),
    onSuccess: () => {
      setMergeSourceId(null);
      setMergeSearch("");
      queryClient.invalidateQueries({ queryKey: ["all-supplier-products", activeBusiness?.id] });
      queryClient.invalidateQueries({ queryKey: ["invoices", activeBusiness?.id] });
    },
  });

  const [aiCleanupResult, setAiCleanupResult] = useState<{ resolved: number; accepted: number; leftForHuman: number } | null>(null);
  const aiCleanupMutation = useMutation({
    mutationFn: () => resolveAllPending(activeBusiness?.id),
    onSuccess: (result) => {
      setAiCleanupResult(result);
      queryClient.invalidateQueries({ queryKey: ["all-supplier-products", activeBusiness?.id] });
    },
  });

  const suppliers = useMemo(() => buildSuppliers(invoices ?? []), [invoices]);

  useEffect(() => {
    const state = location.state as { supplierId?: string } | null;
    if (state?.supplierId && suppliers.length > 0) {
      const match = suppliers.find((s) => s.id === state.supplierId);
      if (match) setSelectedSupplier(match);
    }
  }, [location.state, suppliers]);

  const catalogSuppliers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of allCatalog) {
      if (!seen.has(c.supplierId)) seen.set(c.supplierId, c.supplierName ?? c.supplierId);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allCatalog]);

  const menuGroups = (menuCostingData?.state?.groups ?? { ingredient: [], prep: [], menu: [] }) as { ingredient: string[]; prep: string[]; menu: string[] };
  const activeLayerGroups: string[] = catalogLayer ? (menuGroups[catalogLayer as keyof typeof menuGroups] ?? []) : [];

  const supplierCategories = useMemo(
    () => Array.from(new Set(suppliers.map((s) => s.category || "Other"))).sort((a, b) => a.localeCompare(b)),
    [suppliers],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let items = suppliers;

    if (q) {
      items = items.filter((s) =>
        s.name.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q),
      );
    }

    if (supplierCategoryFilter !== "all") {
      items = items.filter((s) => (s.category || "Other") === supplierCategoryFilter);
    }

    return [...items].sort((a, b) => {
      if (supplierSort === "spend_high") return b.totalSpend - a.totalSpend;
      if (supplierSort === "invoices_high") return b.invoiceCount - a.invoiceCount;
      if (supplierSort === "products_high") return b.products.length - a.products.length;
      if (supplierSort === "recent_order") return (b.lastInvoiceDate || "").localeCompare(a.lastInvoiceDate || "");
      return a.name.localeCompare(b.name);
    });
  }, [search, suppliers, supplierCategoryFilter, supplierSort]);

  const filteredCatalog = useMemo(() => {
    let items = allCatalog;
    if (catalogStatus !== "all") items = items.filter((c) => c.status === catalogStatus);
    if (catalogSupplier !== "all") items = items.filter((c) => c.supplierId === catalogSupplier);
    if (catalogLayer) {
      const layerGroups = (menuGroups[catalogLayer as keyof typeof menuGroups] ?? []).map((g) => g.toLowerCase());
      if (catalogGroup) {
        const groupLower = catalogGroup.toLowerCase();
        items = items.filter((c) => c.tags.some((t) => t.toLowerCase() === groupLower));
      } else {
        items = items.filter((c) => c.tags.some((t) => layerGroups.includes(t.toLowerCase())));
      }
    }
    if (catalogSearch.trim()) {
      const q = catalogSearch.toLowerCase();
      items = items.filter((c) =>
        c.description.toLowerCase().includes(q) ||
        c.productCode.toLowerCase().includes(q) ||
        (c.supplierName ?? "").toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [allCatalog, catalogStatus, catalogSearch, catalogSupplier, catalogLayer, catalogGroup, menuGroups]);

  const pendingCatalogCount = allCatalog.filter((c) => c.status === "new" || c.status === "conflict").length;

  const inboxItems = useMemo(
    () => allCatalog.filter((c) => c.status === "new" || c.status === "conflict"),
    [allCatalog],
  );

  // Map productCode → ingredient info derived from menu costing
  const ingredientMapping = useMemo(() => {
    const map = new Map<string, { ingredientName: string; group: string; layer: string }>();
    const groups = menuCostingData?.state?.groups ?? { ingredient: [], prep: [], menu: [] };
    const layerOf = (group: string) =>
      (groups as Record<string, string[]>).ingredient?.includes(group) ? "Ingredient"
      : (groups as Record<string, string[]>).prep?.includes(group) ? "Prep"
      : (groups as Record<string, string[]>).menu?.includes(group) ? "Menu" : "";
    for (const ing of ((menuCostingData?.state as { ingredients?: { name: string; group: string; components?: { type: string; refId?: string }[] }[] })?.ingredients ?? [])) {
      for (const comp of (ing.components ?? [])) {
        if (comp.type === "supplier_input" && comp.refId) {
          map.set(comp.refId, { ingredientName: ing.name, group: ing.group ?? "", layer: layerOf(ing.group ?? "") });
        }
      }
    }
    return map;
  }, [menuCostingData]);

  // Build a lookup: id → SupplierProduct (for resolving suggestedMatchId labels)
  const catalogById = useMemo(() => new Map(allCatalog.map((c) => [c.id, c])), [allCatalog]);

  // Map "supplierId|productCode" → latest and immediately previous unit prices seen on invoices.
  const latestPriceByProduct = useMemo(() => {
    const history = new Map<string, { unitPrice: number; date: string }[]>();
    for (const inv of invoices) {
      for (const item of inv.lineItems) {
        if (!item.productCode) continue;
        const price = Number(item.unitPrice);
        if (!Number.isFinite(price)) continue;
        const key = `${inv.supplier.id}|${item.productCode}`;
        const date = inv.invoiceDate ?? "";
        const entries = history.get(key) ?? [];
        entries.push({ unitPrice: price, date });
        history.set(key, entries);
      }
    }

    const map = new Map<string, { unitPrice: number; previousUnitPrice?: number; date: string }>();
    for (const [key, entries] of history.entries()) {
      const sorted = entries.sort((a, b) => b.date.localeCompare(a.date));
      map.set(key, {
        unitPrice: sorted[0].unitPrice,
        previousUnitPrice: sorted[1]?.unitPrice,
        date: sorted[0].date,
      });
    }
    return map;
  }, [invoices]);

  const totalProducts = suppliers.reduce((n, s) => n + s.products.length, 0);

  const liveSelectedSupplier = selectedSupplier
    ? (suppliers.find((s) => s.id === selectedSupplier.id) ?? selectedSupplier)
    : null;

  return (
    <div
      className="flex h-full"
      style={{ backgroundColor: CANVAS, minHeight: "calc(100vh - 73px)", fontFamily: "var(--font-ui)" }}
    >
      {mergeOpen && (
        <MergeSuppliersModal suppliers={suppliers} businessId={activeBusiness?.id} onClose={() => setMergeOpen(false)} />
      )}
      {liveSelectedSupplier && (
        <SupplierModal supplier={liveSelectedSupplier} businessId={activeBusiness?.id} categories={categories} onClose={() => setSelectedSupplier(null)} />
      )}

      {/* ── Main content ── */}
      <main className="flex-1 flex flex-col overflow-hidden">

        {pageView === "suppliers" && (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {invoicesError && (
                <div className="px-4 py-3 rounded-lg text-sm" style={{ color: UP, backgroundColor: UP_WEAK, border: `1px solid ${UP}33` }}>
                  Could not load suppliers: {(invoicesError as Error).message}
                </div>
              )}

              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: SURFACE, boxShadow: SHADOW, border: `1px solid ${LINE}` }}>
                <div className="px-6 pb-4 pt-6">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h2 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Suppliers</h2>
                      <p className="text-sm mt-1" style={{ color: NAVY, opacity: 0.55 }}>
                        {suppliers.length} supplier{suppliers.length !== 1 ? "s" : ""} · {totalProducts} products catalogued
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMergeOpen(true)}
                      className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-xs font-bold text-white transition-all"
                      style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
                    >
                      <GitMerge size={13} />
                      Merge suppliers
                    </button>
                  </div>
                </div>

                <div className="px-5 pb-4 pt-2" style={{ borderBottom: `1px solid ${LINE}` }}>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative w-full lg:max-w-[360px]">
                      <Search
                        size={16}
                        className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
                        style={{ color: NAVY, opacity: 0.45 }}
                      />
                      <input
                        type="text"
                        placeholder="Search suppliers"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-10 w-full rounded-md pl-10 pr-8 text-sm outline-none transition-all"
                        style={{
                          border: `1px solid ${search ? NAVY + "55" : "transparent"}`,
                          backgroundColor: "#f7f7f3",
                          color: NAVY,
                        }}
                        onFocus={(e) => (e.target.style.border = `1px solid ${NAVY}55`)}
                        onBlur={(e) => (e.target.style.border = `1px solid ${search ? NAVY + "55" : "transparent"}`)}
                      />
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch("")}
                          className="absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                          style={{ color: NAVY, opacity: 0.45 }}
                          aria-label="Clear supplier search"
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                      <label
                        className="relative inline-flex h-10 min-w-[116px] cursor-pointer items-center justify-between gap-2 rounded-md px-3 text-sm font-bold"
                        style={{ color: NAVY, backgroundColor: "#f7f7f3", border: "1px solid transparent" }}
                      >
                        <span>Filter by</span>
                        <ChevronDown size={14} style={{ opacity: 0.55 }} />
                        <select
                          aria-label="Filter suppliers by category"
                          value={supplierCategoryFilter}
                          onChange={(event) => setSupplierCategoryFilter(event.target.value)}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        >
                          <option value="all">All categories</option>
                          {supplierCategories.map((category) => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </label>

                      <label
                        className="relative inline-flex h-10 min-w-[110px] cursor-pointer items-center justify-between gap-2 rounded-md px-3 text-sm font-bold"
                        style={{ color: NAVY, backgroundColor: "#f7f7f3", border: "1px solid transparent" }}
                      >
                        <span>Sort by</span>
                        <ChevronDown size={14} style={{ opacity: 0.55 }} />
                        <select
                          aria-label="Sort suppliers"
                          value={supplierSort}
                          onChange={(event) => setSupplierSort(event.target.value as SupplierSortRule)}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        >
                          <option value="name_az">Name A-Z</option>
                          <option value="spend_high">Highest spend</option>
                          <option value="invoices_high">Most invoices</option>
                          <option value="products_high">Most products</option>
                          <option value="recent_order">Recent order</option>
                        </select>
                      </label>

                      <div className="inline-flex h-10 items-center rounded-md p-1" style={{ backgroundColor: "#f7f7f3", border: "1px solid transparent" }}>
                        {([
                          { value: "grid", label: "Grid view", icon: <LayoutGrid size={14} /> },
                          { value: "list", label: "List view", icon: <List size={15} /> },
                        ] as const).map((option) => {
                          const active = supplierView === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setSupplierView(option.value)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-all"
                              style={active
                                ? { backgroundColor: ACCENT_WEAK, color: ORANGE }
                                : { color: NAVY, opacity: 0.45 }
                              }
                              aria-label={option.label}
                              title={option.label}
                            >
                              {option.icon}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {filtered.length === 0 ? (
                  <div className="p-5">
                    <div className="py-24 text-center">
                      <Package size={40} className="mx-auto mb-4" style={{ color: NAVY, opacity: 0.18 }} />
                      <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.4 }}>
                        {search || supplierCategoryFilter !== "all" ? "No suppliers match your filters." : "No suppliers found."}
                      </p>
                    </div>
                  </div>
                ) : (
                  supplierView === "grid" ? (
                    <div className="p-5">
                      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
                        {filtered.map((s) => (
                          <SupplierCard key={s.id} supplier={s} onClick={() => setSelectedSupplier(s)} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <div role="table" aria-label="Suppliers" className="min-w-[980px] space-y-2">
                        <div
                          role="row"
                          className="grid items-center gap-4 px-5 py-2.5"
                          style={{ gridTemplateColumns: SUPPLIER_LIST_COLUMNS, borderBottom: `1px solid ${LINE}` }}
                        >
                          {["Supplier name", "Category", "Phone number", "Email", "Last order date", "Status"].map((label) => (
                            <span key={label} role="columnheader" className="text-sm font-bold" style={{ color: NAVY, opacity: 0.42 }}>
                              {label}
                            </span>
                          ))}
                        </div>
                        {filtered.map((s, index) => (
                          <SupplierListRow key={s.id} supplier={s} index={index} onClick={() => setSelectedSupplier(s)} />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        )}

        {pageView === "inbox" && (
          <>
            <div className="px-6 pt-6 pb-4 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}` }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Inbox</h2>
                  <p className="text-sm mt-0.5" style={{ color: NAVY, opacity: 0.55 }}>
                    {inboxItems.length === 0 ? "All items reviewed — nothing to action." : `${inboxItems.length} item${inboxItems.length !== 1 ? "s" : ""} need your review`}
                  </p>
                </div>
                {inboxItems.length > 0 && (
                  <div className="flex items-center gap-3 mt-1">
                    {selectedInboxIds.size > 0 && (
                      <>
                        <span className="text-xs font-semibold" style={{ color: NAVY, opacity: 0.55 }}>{selectedInboxIds.size} selected</span>
                        <button
                          type="button"
                          disabled={batchPending}
                          onClick={async () => {
                            setBatchPending(true);
                            const items = inboxItems.filter((c) => selectedInboxIds.has(c.id));
                            await Promise.all(
                              items.map((c) => resolveSupplierProduct(c.supplierId, c.id, "dismiss", {}, activeBusiness?.id))
                            );
                            await queryClient.invalidateQueries({ queryKey: ["all-supplier-products", activeBusiness?.id] });
                            setSelectedInboxIds(new Set());
                            setBatchPending(false);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-bold transition-all disabled:opacity-50"
                          style={{ backgroundColor: CHIP, color: NAVY }}
                        >
                          {batchPending ? <Loader2 size={13} className="animate-spin" /> : <CheckSquare size={13} />}
                          Confirm all selected
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedInboxIds(new Set())}
                          className="text-xs font-semibold px-2 py-1 rounded-lg transition-all"
                          style={{ color: NAVY, opacity: 0.45 }}
                        >
                          Clear
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const allIds = new Set(inboxItems.map((c) => c.id));
                        const allSelected = inboxItems.every((c) => selectedInboxIds.has(c.id));
                        setSelectedInboxIds(allSelected ? new Set() : allIds);
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-all"
                      style={{ backgroundColor: CHIP, color: NAVY }}
                    >
                      {inboxItems.every((c) => selectedInboxIds.has(c.id))
                        ? <><CheckSquare size={13} /> Deselect all</>
                        : <><Square size={13} /> Select all</>
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>
            {pendingCatalogCount > 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: WARN_WEAK }}>
                <AlertCircle size={13} style={{ color: WARN, flexShrink: 0 }} />
                <p className="text-xs flex-1" style={{ color: WARN }}>
                  {allCatalog.filter((c) => c.status === "conflict").length > 0 && (
                    <><strong>{allCatalog.filter((c) => c.status === "conflict").length} conflict{allCatalog.filter((c) => c.status === "conflict").length !== 1 ? "s" : ""}</strong> need a decision. </>
                  )}
                  {allCatalog.filter((c) => c.status === "new").length > 0 && (
                    <><strong>{allCatalog.filter((c) => c.status === "new").length} new</strong> item{allCatalog.filter((c) => c.status === "new").length !== 1 ? "s" : ""} from the backfill — confirm all to clear the queue.</>
                  )}
                </p>
                <button
                  type="button"
                  disabled={aiCleanupMutation.isPending || catalogResolveMutation.isPending}
                  onClick={() => { setAiCleanupResult(null); aiCleanupMutation.mutate(); }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold flex-shrink-0 transition-all disabled:opacity-50"
                  style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}
                >
                  {aiCleanupMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                  {aiCleanupMutation.isPending ? "Running AI…" : "Run AI cleanup"}
                </button>
                {allCatalog.filter((c) => c.status === "new").length > 0 && (
                  <button
                    type="button"
                    disabled={catalogResolveMutation.isPending || aiCleanupMutation.isPending}
                    onClick={async () => {
                      const newItems = allCatalog.filter((c) => c.status === "new");
                      for (const c of newItems) {
                        await catalogResolveMutation.mutateAsync({ supplierId: c.supplierId, productId: c.id, action: "dismiss" });
                      }
                    }}
                    className="rounded-lg px-3 py-1.5 text-[11px] font-bold flex-shrink-0 transition-all disabled:opacity-50"
                    style={{ backgroundColor: ORANGE, color: "white" }}
                  >
                    {catalogResolveMutation.isPending ? "Confirming…" : "Confirm all new"}
                  </button>
                )}
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {inboxItems.length === 0 ? (
                <div className="py-24 text-center">
                  <CheckCircle size={40} className="mx-auto mb-4" style={{ color: NAVY, opacity: 0.18 }} />
                  <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.4 }}>Inbox clear — new items will appear here as invoices are scanned.</p>
                </div>
              ) : (
                inboxItems.map((c) => {
                  const suggestion = c.suggestedMatchId ? catalogById.get(c.suggestedMatchId) : null;
                  const isConflict = c.status === "conflict";
                  const confidence = c.matchConfidence ?? 0;
                  const confidenceLabel = confidence >= 0.9 ? "High" : confidence >= 0.7 ? "Medium" : "Low";
                  const confidenceColor = confidence >= 0.9 ? DOWN : WARN;
                  return (
                    <div
                      key={c.id}
                      className="rounded-lg px-4 py-3"
                      style={{
                        backgroundColor: SURFACE,
                        border: `1px solid ${selectedInboxIds.has(c.id) ? ACCENT_LINE : isConflict ? `${WARN}55` : LINE}`,
                        boxShadow: selectedInboxIds.has(c.id) ? `0 0 0 2px ${ACCENT_WEAK}, ${SHADOW}` : SHADOW,
                      }}
                    >
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-2">
                        <button
                          type="button"
                          onClick={() => setSelectedInboxIds((prev) => {
                            const next = new Set(prev);
                            next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                            return next;
                          })}
                          className="flex-shrink-0 transition-colors"
                          style={{ color: selectedInboxIds.has(c.id) ? ORANGE : MUTED }}
                        >
                          {selectedInboxIds.has(c.id) ? <CheckSquare size={14} /> : <Square size={14} />}
                        </button>
                        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold tabular-nums" style={{ color: NAVY }}>{c.productCode}</span>
                          {isConflict ? (
                            <span className="rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: WARN_WEAK, color: WARN }}>Conflict</span>
                          ) : (
                            <span className="rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: ACCENT_WEAK, color: ORANGE }}>New</span>
                          )}
                          <span className="text-xs font-medium leading-snug truncate" style={{ color: NAVY }}>{c.description}</span>
                          <span className="text-[11px]" style={{ color: NAVY, opacity: 0.45 }}>{c.supplierName}</span>
                        </div>
                      </div>

                      {/* Conflict sub-section */}
                      {isConflict && (
                        <div className="mb-2 px-2.5 py-1.5 rounded-md" style={{ backgroundColor: WARN_WEAK, border: `1px dashed ${WARN}55` }}>
                          <div className="flex gap-6 text-xs">
                            <div><span className="font-bold uppercase text-[10px]" style={{ color: WARN }}>Stored </span><span style={{ color: NAVY, opacity: 0.7 }}>{c.description}</span></div>
                            <div><span className="font-bold uppercase text-[10px]" style={{ color: ORANGE }}>New </span><span style={{ color: NAVY }}>{c.conflictDescription}</span></div>
                          </div>
                        </div>
                      )}

                      {/* AI suggestion */}
                      {suggestion && (
                        <div className="mb-2 px-2.5 py-1.5 rounded-md" style={{ backgroundColor: ACCENT_WEAK, border: `1px solid ${ACCENT_LINE}` }}>
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <Sparkles size={12} style={{ color: ORANGE, flexShrink: 0 }} />
                            <span className="text-xs font-bold" style={{ color: ORANGE }}>Likely scan error</span>
                            <span className="rounded-full px-1.5 py-px text-[9px] font-bold" style={{ backgroundColor: `${confidenceColor}18`, color: confidenceColor }}>{confidenceLabel} ({Math.round(confidence * 100)}%)</span>
                            <span className="text-[11px]" style={{ color: NAVY, opacity: 0.6 }}>→ <span className="font-bold tabular-nums">{suggestion.productCode}</span> {suggestion.description}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={catalogResolveMutation.isPending}
                              onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "dismiss" }, {
                                onSuccess: () => { setMergeSourceId(c.id); }
                              })}
                              className="rounded px-2.5 py-1 text-[11px] font-bold transition-all disabled:opacity-50"
                              style={{ backgroundColor: ORANGE, color: "white" }}
                            >
                              Accept — merge into {suggestion.productCode}
                            </button>
                            <button
                              type="button"
                              disabled={catalogResolveMutation.isPending}
                              onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "dismiss" })}
                              className="rounded px-2.5 py-1 text-[11px] font-bold transition-all disabled:opacity-50"
                              style={{ backgroundColor: CHIP, color: NAVY }}
                            >
                              Dismiss — keep as new item
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Standard actions */}
                      <div className="flex items-center gap-1.5">
                        {isConflict ? (<>
                          <button
                            type="button"
                            disabled={catalogResolveMutation.isPending}
                            onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "confirm", description: c.conflictDescription ?? undefined })}
                            className="rounded px-2.5 py-1 text-[11px] font-bold transition-all disabled:opacity-50"
                            style={{ backgroundColor: ACCENT_WEAK, color: ORANGE }}
                          >
                            Use new description
                          </button>
                          <button
                            type="button"
                            disabled={catalogResolveMutation.isPending}
                            onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "dismiss" })}
                            className="rounded px-2.5 py-1 text-[11px] font-bold transition-all disabled:opacity-50"
                            style={{ backgroundColor: CHIP, color: NAVY }}
                          >
                            Keep existing
                          </button>
                        </>) : !suggestion && (
                          <button
                            type="button"
                            disabled={catalogResolveMutation.isPending}
                            onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "dismiss" })}
                            className="rounded px-2.5 py-1 text-[11px] font-bold transition-all disabled:opacity-50"
                            style={{ backgroundColor: CHIP, color: NAVY }}
                          >
                            Mark as standalone
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => { setMergeSourceId(c.id); setMergeSearch(""); }}
                          className="rounded px-2.5 py-1 text-[11px] font-semibold transition-all flex items-center gap-1"
                          style={{ backgroundColor: CHIP, color: NAVY, opacity: 0.75 }}
                        >
                          <Merge size={10} /> Merge manually
                        </button>
                        {(() => {
                          const allSupplierInvs = (invoices ?? []).filter((inv) =>
                            inv.supplier.id === c.supplierId &&
                            inv.lineItems.some((li) => li.productCode === c.productCode)
                          );
                          const oldInv = allSupplierInvs.find((inv) =>
                            inv.lineItems.some((li) => li.productCode === c.productCode && li.description === c.description)
                          );
                          const newInv = isConflict ? allSupplierInvs.find((inv) =>
                            inv.lineItems.some((li) => li.productCode === c.productCode && li.description === c.conflictDescription)
                          ) : null;
                          return (<>
                            {oldInv && (
                              <button
                                type="button"
                                onClick={() => setViewInvoice({ invoiceId: oldInv.id, productCode: c.productCode })}
                                className="rounded px-2.5 py-1 text-[11px] font-semibold transition-all flex items-center gap-1"
                                style={{ backgroundColor: CHIP, color: NAVY, opacity: 0.75 }}
                              >
                                <Link2 size={10} /> {isConflict ? "Old invoice" : "View invoice"}
                              </button>
                            )}
                            {newInv && (
                              <button
                                type="button"
                                onClick={() => setViewInvoice({ invoiceId: newInv.id, productCode: c.productCode })}
                                className="rounded px-2.5 py-1 text-[11px] font-semibold transition-all flex items-center gap-1"
                                style={{ backgroundColor: CHIP, color: NAVY, opacity: 0.75 }}
                              >
                                <Link2 size={10} /> New invoice
                              </button>
                            )}
                          </>);
                        })()}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {pageView === "catalog" && (
          <>
            <div className="px-6 pt-6 pb-4 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}` }}>
              <div className="mb-3">
                <div>
                  <h2 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Item Catalog</h2>
                  <p className="text-sm mt-0.5" style={{ color: NAVY, opacity: 0.55 }}>
                    {filteredCatalog.length !== allCatalog.length
                      ? `${filteredCatalog.length} of ${allCatalog.length} items`
                      : `${allCatalog.length} item${allCatalog.length !== 1 ? "s" : ""} across all suppliers`}
                  </p>
                </div>
              </div>
              {/* Filter bar */}
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={catalogSupplier}
                  onChange={(e) => setCatalogSupplier(e.target.value)}
                  className="rounded-lg px-3 py-1.5 text-xs outline-none"
                  style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                >
                  <option value="all">All suppliers</option>
                  {catalogSuppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <select
                  value={catalogLayer}
                  onChange={(e) => { setCatalogLayer(e.target.value); setCatalogGroup(""); }}
                  className="rounded-lg px-3 py-1.5 text-xs outline-none"
                  style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                >
                  <option value="">All layers</option>
                  <option value="ingredient">Ingredient</option>
                  <option value="prep">Prep</option>
                  <option value="menu">Menu</option>
                </select>
                <select
                  value={catalogStatus}
                  onChange={(e) => setCatalogStatus(e.target.value)}
                  className="rounded-lg px-3 py-1.5 text-xs outline-none"
                  style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                >
                  <option value="all">All statuses</option>
                  <option value="new">New</option>
                  <option value="conflict">Conflict</option>
                  <option value="confirmed">Confirmed</option>
                </select>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: NAVY, opacity: 0.4 }} />
                  <input
                    type="text"
                    placeholder="Search items…"
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    className="pl-8 pr-4 py-1.5 text-xs rounded-lg outline-none"
                    style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE, width: 200 }}
                    onFocus={(e) => (e.target.style.border = `1.5px solid ${ORANGE}`)}
                    onBlur={(e) => (e.target.style.border = `1.5px solid ${LINE}`)}
                  />
                </div>
                {catalogLayer && activeLayerGroups.length > 0 && (
                  <select
                    value={catalogGroup}
                    onChange={(e) => setCatalogGroup(e.target.value)}
                    className="rounded-lg px-3 py-1.5 text-xs outline-none"
                    style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                  >
                    <option value="">All {catalogLayer} groups</option>
                    {activeLayerGroups.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                )}
                {(catalogSupplier !== "all" || catalogLayer || catalogSearch || catalogStatus !== "all") && (
                  <button
                    type="button"
                    onClick={() => { setCatalogSupplier("all"); setCatalogLayer(""); setCatalogGroup(""); setCatalogSearch(""); setCatalogStatus("all"); }}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all"
                    style={{ color: NAVY, opacity: 0.6, backgroundColor: CHIP }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">

              {aiCleanupResult && (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs" style={{ backgroundColor: DOWN_WEAK, border: `1px solid ${DOWN}33` }}>
                  <CheckCircle size={13} style={{ color: ORANGE, flexShrink: 0 }} />
                  <span style={{ color: DOWN }}>
                    AI cleanup done — <strong>{aiCleanupResult.resolved}</strong> auto-resolved, <strong>{aiCleanupResult.accepted}</strong> description{aiCleanupResult.accepted !== 1 ? "s" : ""} updated, <strong>{aiCleanupResult.leftForHuman}</strong> need your review.
                  </span>
                  <button type="button" onClick={() => setAiCleanupResult(null)} className="ml-auto" style={{ color: DOWN, opacity: 0.5 }}><X size={12} /></button>
                </div>
              )}

              {/* Table — single source of truth */}
              {filteredCatalog.length === 0 ? (
                <div className="py-24 text-center">
                  <Package size={40} className="mx-auto mb-4" style={{ color: NAVY, opacity: 0.18 }} />
                  <p className="text-sm font-medium" style={{ color: NAVY, opacity: 0.4 }}>
                    {catalogSearch || catalogStatus !== "all" || catalogSupplier !== "all" || catalogGroup ? "No items match your filters." : "No catalog entries yet — they'll appear as invoices are scanned."}
                  </p>
                </div>
              ) : (
                <div className="rounded-xl overflow-hidden" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
                  <table className="w-full table-fixed text-sm">
                    <colgroup>
                      <col className="w-[9%]" /><col className="w-[6%]" /><col className="w-[11%]" /><col className="w-[18%]" /><col className="w-[13%]" /><col className="w-[13%]" /><col className="w-[9%]" /><col className="w-[9%]" /><col className="w-[12%]" />
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                        {[
                          { label: "Code",        cls: "text-left" },
                          { label: "Status",       cls: "text-left" },
                          { label: "Supplier",     cls: "text-left" },
                          { label: "Description",  cls: "text-left" },
                          { label: "Tags",         cls: "text-left" },
                          { label: "Ingredient",   cls: "text-left" },
                          { label: "Latest Price", cls: "text-right" },
                          { label: "Last Seen",    cls: "text-center" },
                          { label: "Actions",      cls: "text-right" },
                        ].map(({ label, cls }) => (
                          <th key={label} className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-wide ${cls}`} style={{ color: NAVY, opacity: 0.45 }}>
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCatalog.map((c) => {
                        const isTagging = tagInput === c.id;
                        const isConflict = c.status === "conflict";
                        const isExpanded = expandedCatalogId === c.id;
                        const invoiceHistory = isExpanded
                          ? (invoices ?? []).flatMap((inv) =>
                              inv.lineItems
                                .filter((item) => item.productCode === c.productCode && inv.supplier.id === c.supplierId)
                                .map((item) => ({
                                  invoiceId: inv.id,
                                  invoiceNumber: inv.invoiceNumber,
                                  invoiceDate: inv.invoiceDate,
                                  quantity: item.quantity,
                                  unit: item.unit,
                                  unitPrice: item.unitPrice,
                                  totalPrice: item.totalPrice,
                                }))
                            ).sort((a, b) => (b.invoiceDate ?? "").localeCompare(a.invoiceDate ?? ""))
                          : [];
                        return (
                          <>
                            <tr
                              key={c.id}
                              onClick={() => setExpandedCatalogId(isExpanded ? null : c.id)}
                              style={{ borderTop: `1px solid ${LINE}`, backgroundColor: isExpanded ? ACCENT_WEAK : isConflict ? WARN_WEAK : undefined, cursor: "pointer" }}
                              onMouseEnter={(e) => !isConflict && !isExpanded && (e.currentTarget.style.backgroundColor = ACCENT_WEAK)}
                              onMouseLeave={(e) => !isConflict && !isExpanded && (e.currentTarget.style.backgroundColor = "")}
                            >
                              <td className="px-4 py-3">
                                {(() => {
                                  const latest = latestPriceByProduct.get(`${c.supplierId}|${c.productCode}`);
                                  return (
                                    <span className="inline-flex items-center gap-1.5">
                                      <span className="inline-flex w-3 justify-center">
                                        <PriceDirectionIcon current={latest?.unitPrice} previous={latest?.previousUnitPrice} />
                                      </span>
                                      <span className="text-xs font-semibold tabular-nums" style={{ color: NAVY, opacity: 0.7 }}>{c.productCode}</span>
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3">
                                {isConflict && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: WARN_WEAK, color: WARN }}>Conflict</span>}
                                {c.status === "new" && <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ backgroundColor: ACCENT_WEAK, color: ORANGE }}>New</span>}
                                {c.status === "confirmed" && <CheckCircle size={13} style={{ color: DOWN }} />}
                              </td>
                              <td className="px-4 py-3 text-xs truncate" style={{ color: NAVY, opacity: 0.6 }}>{c.supplierName}</td>
                              <td className="px-4 py-3 text-xs leading-snug" style={{ color: NAVY }}>{c.description}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap items-center gap-1">
                                  {c.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[9px] font-semibold cursor-pointer"
                                      style={{ backgroundColor: `${NAVY}10`, color: NAVY }}
                                      onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "confirm", tags: c.tags.filter((t) => t !== tag) })}
                                    >
                                      {tag}<X size={8} />
                                    </span>
                                  ))}
                                  {isTagging ? (
                                    <input
                                      autoFocus
                                      value={tagDraft}
                                      onChange={(e) => setTagDraft(e.target.value)}
                                      onKeyDown={(e) => {
                                        if ((e.key === "Enter" || e.key === ",") && tagDraft.trim()) {
                                          e.preventDefault();
                                          catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "confirm", tags: [...c.tags, tagDraft.trim()] });
                                          setTagDraft("");
                                          setTagInput(null);
                                        }
                                        if (e.key === "Escape") { setTagDraft(""); setTagInput(null); }
                                      }}
                                      onBlur={() => { setTagDraft(""); setTagInput(null); }}
                                      placeholder="tag…"
                                      className="rounded px-1.5 py-0.5 text-[10px] outline-none w-16"
                                      style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                                    />
                                  ) : (
                                    <button type="button" onClick={() => setTagInput(c.id)} className="rounded-full p-0.5 opacity-30 hover:opacity-80 transition-opacity" style={{ color: NAVY }}>
                                      <Plus size={10} />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {(() => {
                                  const mapping = ingredientMapping.get(c.productCode);
                                  if (!mapping) return (
                                    <span className="text-[10px] flex items-center gap-1" style={{ color: NAVY, opacity: 0.3 }}>
                                      <Link2 size={9} />Unassigned
                                    </span>
                                  );
                                  return (
                                    <div>
                                      <p className="text-xs font-semibold truncate" style={{ color: NAVY }}>{mapping.ingredientName}</p>
                                      <p className="text-[10px] truncate" style={{ color: NAVY, opacity: 0.45 }}>{mapping.layer} · {mapping.group}</p>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 text-right text-xs tabular-nums" style={{ color: NAVY, opacity: 0.75 }}>
                                {(() => {
                                  const latest = latestPriceByProduct.get(`${c.supplierId}|${c.productCode}`);
                                  return latest ? `$${latest.unitPrice.toFixed(2)}` : "—";
                                })()}
                              </td>
                              <td className="px-4 py-3 text-center text-xs" style={{ color: NAVY, opacity: 0.5 }}>
                                {c.lastSeenAt ? new Date(c.lastSeenAt).toLocaleDateString("en-AU", { day: "2-digit", month: "short" }) : "—"}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  {isConflict && (<>
                                    <button
                                      type="button"
                                      disabled={catalogResolveMutation.isPending}
                                      onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "confirm", description: c.conflictDescription ?? undefined })}
                                      className="rounded-md px-2 py-1 text-[10px] font-bold transition-all disabled:opacity-50"
                                      style={{ backgroundColor: ACCENT_WEAK, color: ORANGE }}
                                    >
                                      Use new
                                    </button>
                                    <button
                                      type="button"
                                      disabled={catalogResolveMutation.isPending}
                                      onClick={() => catalogResolveMutation.mutate({ supplierId: c.supplierId, productId: c.id, action: "dismiss" })}
                                      className="rounded-md px-2 py-1 text-[10px] font-bold transition-all disabled:opacity-50"
                                      style={{ backgroundColor: CHIP, color: NAVY }}
                                    >
                                      Keep
                                    </button>
                                  </>)}
                                  {/* Merge */}
                                  <button
                                    type="button"
                                    title="Merge into another item"
                                    onClick={(e) => { e.stopPropagation(); setMergeSourceId(c.id); setMergeSearch(""); }}
                                    className="rounded-md p-1.5 transition-all opacity-30 hover:opacity-80"
                                    style={{ color: NAVY }}
                                  >
                                    <Merge size={12} />
                                  </button>
                                  {/* Delete */}
                                  {confirmDeleteId === c.id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        disabled={deleteMutation.isPending}
                                        onClick={() => deleteMutation.mutate({ supplierId: c.supplierId, productId: c.id })}
                                        className="rounded-md px-2 py-1 text-[10px] font-bold transition-all disabled:opacity-50"
                                        style={{ backgroundColor: UP_WEAK, color: UP }}
                                      >
                                        {deleteMutation.isPending ? "…" : "Confirm"}
                                      </button>
                                      <button type="button" onClick={() => setConfirmDeleteId(null)} className="rounded-md p-1 opacity-50 hover:opacity-100" style={{ color: NAVY }}>
                                        <X size={11} />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      title="Delete this entry"
                                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(c.id); }}
                                      className="rounded-md p-1.5 transition-all opacity-30 hover:opacity-80"
                                      style={{ color: UP }}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {/* Conflict detail sub-row */}
                            {isConflict && (
                              <tr style={{ backgroundColor: WARN_WEAK, borderTop: `1px dashed ${WARN}55` }}>
                                <td colSpan={9} className="px-4 pb-3 pt-1">
                                  <div className="flex items-start gap-6 text-xs">
                                    <div className="flex items-start gap-2">
                                      <span className="text-[10px] font-bold uppercase tracking-wide flex-shrink-0 mt-0.5" style={{ color: WARN }}>Stored</span>
                                      <span style={{ color: NAVY, opacity: 0.6 }}>{c.description}</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                      <span className="text-[10px] font-bold uppercase tracking-wide flex-shrink-0 mt-0.5" style={{ color: ORANGE }}>New</span>
                                      <span style={{ color: NAVY }}>{c.conflictDescription}</span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {/* Invoice history sub-row */}
                            {isExpanded && (
                              <tr style={{ backgroundColor: RAISED, borderTop: `1px solid ${LINE}` }}>
                                <td colSpan={9} className="px-4 py-3">
                                  {invoiceHistory.length === 0 ? (
                                    <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>No invoice lines found for this product code.</p>
                                  ) : (
                                    <div>
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr>
                                            {["Invoice #", "Date", "Qty", "Unit", "Unit Price", "Total", ""].map((h) => (
                                              <th key={h} className="text-left pb-1.5 pr-6 font-semibold" style={{ color: NAVY, opacity: 0.4, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {invoiceHistory.map((line, i) => {
                                            const previousLine = invoiceHistory[i + 1];
                                            return (
                                              <tr key={i} style={{ borderTop: i > 0 ? `1px solid ${LINE}` : undefined }}>
                                                <td className="py-1.5 pr-6 font-semibold tabular-nums" style={{ color: NAVY }}>{line.invoiceNumber ?? "—"}</td>
                                                <td className="py-1.5 pr-6" style={{ color: NAVY, opacity: 0.6 }}>{line.invoiceDate ? new Date(line.invoiceDate).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                                                <td className="py-1.5 pr-6" style={{ color: NAVY }}>{line.quantity ?? "—"}</td>
                                                <td className="py-1.5 pr-6" style={{ color: NAVY, opacity: 0.6 }}>{line.unit ?? "—"}</td>
                                                <td className="py-1.5 pr-6 font-semibold" style={{ color: NAVY }}>
                                                  <span className="inline-flex items-center gap-1.5">
                                                    <span className="inline-flex w-3 justify-center">
                                                      <PriceDirectionIcon current={line.unitPrice} previous={previousLine?.unitPrice} />
                                                    </span>
                                                    <span>{line.unitPrice != null ? `$${line.unitPrice.toFixed(2)}` : "—"}</span>
                                                  </span>
                                                </td>
                                                <td className="py-1.5 pr-6 font-bold" style={{ color: NAVY }}>{line.totalPrice != null ? `$${line.totalPrice.toFixed(2)}` : "—"}</td>
                                                <td className="py-1.5">
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); setViewInvoice({ invoiceId: line.invoiceId, productCode: c.productCode }); }}
                                                    className="rounded-md px-2 py-1 text-[10px] font-bold transition-all"
                                                    style={{ backgroundColor: CHIP, color: NAVY }}
                                                  >
                                                    View
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* ── Invoice view modal ── */}
      {viewInvoice && (() => {
        const inv = (invoices ?? []).find((i) => i.id === viewInvoice.invoiceId);
        if (!inv) return null;
        return (
          <InvoiceSlidingPanel
            inv={inv}
            businessId={activeBusiness?.id}
            categories={categories}
            deletesExternalOnInvoiceDelete={deletesExternalOnInvoiceDelete}
            highlightProductCode={viewInvoice.productCode}
            onClose={() => setViewInvoice(null)}
          />
        );
      })()}

      {/* ── Merge modal ── */}
      {mergeSourceId && (() => {
        const source = allCatalog.find((c) => c.id === mergeSourceId);
        if (!source) return null;
        const candidates = allCatalog.filter((c) =>
          c.id !== mergeSourceId &&
          c.supplierId === source.supplierId &&
          (mergeSearch.trim() === "" ||
            c.description.toLowerCase().includes(mergeSearch.toLowerCase()) ||
            c.productCode.toLowerCase().includes(mergeSearch.toLowerCase()))
        );
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ backgroundColor: BACKDROP }}
            onClick={(e) => { if (e.target === e.currentTarget) { setMergeSourceId(null); setMergeSearch(""); } }}
          >
            <div className="w-full max-w-lg rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: "70vh", backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
              <div className="px-5 py-4 flex items-start justify-between gap-4" style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                <div>
                  <p className="text-sm font-extrabold" style={{ color: NAVY }}>Merge into correct item</p>
                  <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.5 }}>
                    <span className="font-semibold tabular-nums">{source.productCode}</span> · {source.description}
                  </p>
                  <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: NAVY, opacity: 0.45 }}>
                    Pick another item from this supplier&apos;s catalog. All invoice lines with this code will be reassigned to that item, then this entry will be deleted.
                  </p>
                </div>
                <button type="button" onClick={() => { setMergeSourceId(null); setMergeSearch(""); }} className="rounded-md p-1.5" style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
                  <X size={14} />
                </button>
              </div>

              <div className="px-5 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: NAVY, opacity: 0.4 }} />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search by code or description…"
                    value={mergeSearch}
                    onChange={(e) => setMergeSearch(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 text-xs rounded-lg outline-none"
                    style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                    onFocus={(e) => (e.target.style.border = `1.5px solid ${ORANGE}`)}
                    onBlur={(e) => (e.target.style.border = `1.5px solid ${LINE}`)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto divide-y" style={{ borderColor: LINE }}>
                {candidates.length === 0 ? (
                  <p className="px-5 py-8 text-center text-xs" style={{ color: NAVY, opacity: 0.4 }}>
                    {mergeSearch ? "No matching items." : "No other items from this supplier."}
                  </p>
                ) : candidates.map((target) => (
                  <button
                    key={target.id}
                    type="button"
                    disabled={mergeMutation.isPending}
                    onClick={() => mergeMutation.mutate({ supplierId: source.supplierId, productId: source.id, mergeIntoProductId: target.id })}
                    className="w-full px-5 py-3.5 text-left transition-colors disabled:opacity-50"
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ACCENT_WEAK)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                  >
                    <p className="text-[11px] font-semibold mb-0.5 tabular-nums" style={{ color: NAVY, opacity: 0.5 }}>{target.productCode}</p>
                    <p className="text-xs leading-snug" style={{ color: NAVY }}>{target.description}</p>
                  </button>
                ))}
              </div>

              {mergeMutation.isPending && (
                <div className="px-5 py-3 flex items-center gap-2" style={{ borderTop: `1px solid ${LINE}` }}>
                  <Loader2 size={13} className="animate-spin" style={{ color: ORANGE }} />
                  <span className="text-xs" style={{ color: NAVY, opacity: 0.6 }}>Merging and reassigning invoice lines…</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
